import { createSelector } from 'reselect';
import * as loadingState from '@dragonfruit/constants/loadingState';
import { transformToOptionFormat } from '@dragonfruit/utils/tutorAuditFields';

export const FETCH_TUTOR_AUDIT_INFO = 'FETCH_TUTOR_AUDIT_INFO';
export const FETCH_TUTOR_AUDIT_INFO_SUCCESS = 'FETCH_TUTOR_AUDIT_INFO_SUCCESS';
export const FETCH_TUTOR_AUDIT_INFO_ERROR = 'FETCH_TUTOR_AUDIT_INFO_ERROR';
export const FETCH_AUDIT_COLLEGES = 'FETCH_AUDIT_COLLEGES';
export const FETCH_AUDIT_COLLEGES_SUCCESS = 'FETCH_AUDIT_COLLEGES_SUCCESS';
export const FETCH_AUDIT_COLLEGES_FAILURE = 'FETCH_AUDIT_COLLEGES_FAILURE';
export const FETCH_AUDIT_MAJORS = 'FETCH_AUDIT_MAJORS';
export const FETCH_AUDIT_MAJORS_SUCCESS = 'FETCH_AUDIT_MAJORS_SUCCESS';
export const FETCH_AUDIT_MAJORS_FAILURE = 'FETCH_AUDIT_MAJORS_FAILURE';

export const UPDATE_TUTOR_CERTIFICATE = 'UPDATE_TUTOR_CERTIFICATE';
export const UPDATE_SUCCESS = 'UPDATE_SUCCESS';
export const UPDATE_ERROR = 'UPDATE_ERROR';
export const CONFIRM_TUTOR_AUDIT = 'CONFIRM_TUTOR_AUDIT';
export const CONFIRM_TUTOR_AUDIT_SUCCESS = 'CONFIRM_TUTOR_AUDIT_SUCCESS';
export const CONFIRM_TUTOR_AUDIT_FAILURE = 'CONFIRM_TUTOR_AUDIT_FAILURE';

export const fetchTutorAuditInfo = () => ({ type: FETCH_TUTOR_AUDIT_INFO });

export const fetchTutorAuditInfoSuccess = ({
  subjects,
  qualifications,
  certificatesRequired,
  tutorHandbookUrl,
  introduction,
  selectedSubjectIds,
  finishedCertificateIds,
  finishedQualificationIds,
  phoneVerificationRequired,
}) => ({
  type: FETCH_TUTOR_AUDIT_INFO_SUCCESS,
  payload: {
    subjects,
    qualifications,
    certificatesRequired,
    tutorHandbookUrl,
    introduction,
    selectedSubjectIds,
    finishedCertificateIds,
    finishedQualificationIds,
    phoneVerificationRequired,
  },
});

export const fetchTutorAuditInfoError = errorMessage => ({
  type: FETCH_TUTOR_AUDIT_INFO_ERROR,
  payload: {
    errorMessage,
  },
});

export const updateSuccess = () => ({ type: UPDATE_SUCCESS });

export const updateError = errorMessage => ({
  type: UPDATE_ERROR,
  payload: {
    errorMessage,
  },
});

export const updateTutorCertificate = ({
  certificateFile,
  certificateTypeId,
}) => ({
  type: UPDATE_TUTOR_CERTIFICATE,
  payload: {
    certificateFile,
    certificateTypeId,
  },
});

export const fetchAuditColleges = () => ({
  type: FETCH_AUDIT_COLLEGES,
});

export const fetchAuditCollegesSuccess = auditCollegeList => ({
  type: FETCH_AUDIT_COLLEGES_SUCCESS,
  payload: {
    auditCollegeList,
  },
});

export const fetchAuditCollegesFailure = () => ({
  type: FETCH_AUDIT_COLLEGES_FAILURE,
});

export const fetchAuditMajors = collegeId => ({
  type: FETCH_AUDIT_MAJORS,
  payload: {
    collegeId,
  },
});

export const fetchAuditMajorsSuccess = auditMajorList => ({
  type: FETCH_AUDIT_MAJORS_SUCCESS,
  payload: {
    auditMajorList,
  },
});

export const fetchAuditMajorsFailure = errorMessage => ({
  type: FETCH_AUDIT_MAJORS_FAILURE,
  payload: {
    errorMessage,
  },
});

export const confirmTutorAudit = () => ({
  type: CONFIRM_TUTOR_AUDIT,
});

export const confirmTutorAuditSuccess = () => ({
  type: CONFIRM_TUTOR_AUDIT_SUCCESS,
});

export const confirmTutorAuditFailure = errorMessage => ({
  type: CONFIRM_TUTOR_AUDIT_FAILURE,
  payload: {
    errorMessage,
  },
});

const INIT_STATE = {
  status: loadingState.EMPTY,
  subjects: [],
  qualifications: [],
  certificatesRequired: [],
  tutorHandbookUrl: '',
  errorMessage: '',
  selectedSubjectIds: [],
  finishedCertificateIds: [],
  finishedQualificationIds: [],
  auditCollegeList: [],
  auditMajorList: [],
  phoneVerificationRequired: false,
};

const reducer = (state = INIT_STATE, action) => {
  switch (action.type) {
    case FETCH_AUDIT_MAJORS:
    case FETCH_AUDIT_COLLEGES:
    case FETCH_TUTOR_AUDIT_INFO:
      return {
        ...state,
        status: loadingState.LOADING,
      };
    case FETCH_AUDIT_COLLEGES_SUCCESS: {
      const { auditCollegeList } = action.payload;
      return {
        ...state,
        auditCollegeList: auditCollegeList.map(transformToOptionFormat),
      };
    }
    case FETCH_AUDIT_MAJORS_SUCCESS: {
      const { auditMajorList } = action.payload;
      return {
        ...state,
        auditMajorList: auditMajorList.map(transformToOptionFormat),
      };
    }

    case FETCH_TUTOR_AUDIT_INFO_SUCCESS:
      return {
        ...state,
        status: loadingState.READY,
        errorMessage: '',
        ...action.payload,
      };

    case FETCH_AUDIT_COLLEGES_FAILURE:
    case FETCH_AUDIT_MAJORS_FAILURE:
    case FETCH_TUTOR_AUDIT_INFO_ERROR:
      return {
        ...state,
        status: loadingState.ERROR,
        errorMessage: action.payload.errorMessage,
      };

    case UPDATE_TUTOR_CERTIFICATE:
      return {
        ...state,
        status: loadingState.LOADING,
      };
    case UPDATE_SUCCESS:
      return {
        ...state,
        status: loadingState.READY,
      };
    case UPDATE_ERROR:
      return {
        ...state,
        status: loadingState.LOADING,
        errorMessage: action.payload.errorMessage,
      };

    case CONFIRM_TUTOR_AUDIT:
      return {
        ...state,
        status: loadingState.LOADING,
      };
    case CONFIRM_TUTOR_AUDIT_SUCCESS:
      return {
        ...state,
        status: loadingState.READY,
      };
    case CONFIRM_TUTOR_AUDIT_FAILURE:
      return {
        ...state,
        status: loadingState.ERROR,
        errorMessage: action.payload.errorMessage,
      };

    default:
      return state;
  }
};

export default reducer;

export const getTutorSubjects = state => state.tutorAudit.subjects;
export const getTutorQualifications = state => state.tutorAudit.qualifications;
export const getTutorCertificatesRequired = state =>
  state.tutorAudit.certificatesRequired;
export const getTutorHandbookUrl = state => state.tutorAudit.tutorHandbookUrl;
export const getSelectedSubjectIds = state =>
  state.tutorAudit.selectedSubjectIds;
export const getLoadingStatus = state => state.tutorAudit.status;
export const getFinishedCertificateIds = state =>
  state.tutorAudit.finishedCertificateIds;
export const getFinishedQualificationIds = state =>
  state.tutorAudit.finishedQualificationIds;

export const getUnfinishedCertificated = createSelector(
  getTutorCertificatesRequired,
  getFinishedCertificateIds,
  (certs, finishedIds) =>
    certs
      .filter(p => p.required)
      .filter(p => !finishedIds.some(id => id === p.id))
);

export const getPhoneVerificationRequired = state =>
  state.tutorAudit.phoneVerificationRequired;
export const getAuditCollegeList = state => state.tutorAudit.auditCollegeList;
export const getAuditMajorList = state => state.tutorAudit.auditMajorList;



// WEBPACK FOOTER //
// ./app/redux/tutorAudit.js